import { isUnderdog, luckyClover } from '@tigrinho/engine';
import { useEffect, useRef, useState } from 'react';
import type { Phrase, TauntItem } from '@tigrinho/server/protocol';
import { HELD_EMOJI, HELD_TEXT, PHRASE_TEXT, SEAT_COLORS, TAUNTS } from '../lib/content';
import { useHold, useRoomCtx } from '../screens/RoomScreen';
import { playerEffects } from './selectors';
import { PawnFace } from './PawnFace';
import { ITEM_ART } from '../lib/art';
import { moneyNumber } from '../lib/format';
import { useCelebrating } from './useCelebrating';
import { prefs } from '../lib/storage';

export type Corner = 'tl' | 'tr' | 'bl' | 'br';

/** Quem fica em cada canto: você embaixo à direita (como no Business Tour), os outros na ordem da mesa. */
export function cornersOf(seatIds: string[], you: string | null): Record<string, Corner> {
  const order: Corner[] = ['tl', 'tr', 'bl'];
  const out: Record<string, Corner> = {};
  const start = you ? seatIds.indexOf(you) : -1;
  if (start < 0) {
    (['tl', 'tr', 'br', 'bl'] as Corner[]).forEach((c, k) => seatIds[k] && (out[seatIds[k]!] = c));
    return out;
  }
  out[you!] = 'br';
  for (let k = 1; k < seatIds.length; k++) out[seatIds[(start + k) % seatIds.length]!] = order[k - 1]!;
  return out;
}

/** Os 4 placares nos cantos da tela. Tocar num adversário abre o Arsenal de Provocação. */
export function Players({ onPhrases }: { onPhrases: () => void }) {
  const { room, you, money, seatIndex, bus } = useRoomCtx();
  const g = room.game!;
  const [target, setTarget] = useState<string | null>(null);
  const [bubbles, setBubbles] = useState<Record<string, { text: string; at: number }>>({});

  useEffect(
    () =>
      bus.onPhrase((m) => {
        const at = Date.now();
        setBubbles((b) => ({ ...b, [m.from]: { text: PHRASE_TEXT[m.phrase], at } }));
        setTimeout(() => setBubbles((b) => (b[m.from]?.at === at ? omit(b, m.from) : b)), 3200);
      }),
    [bus],
  );

  const party = useCelebrating();
  const corners = cornersOf(
    room.seats.map((s) => s.id).filter((id) => g.players.some((p) => p.id === id)),
    you,
  );

  return (
    <>
      {g.players.map((p) => {
        const seat = room.seats.find((s) => s.id === p.id);
        const idx = seatIndex(p.id);
        const fx = playerEffects(g, p.id);
        const turn = g.players[g.current]?.id === p.id && g.phase !== 'finished';
        const isMe = p.id === you;
        const corner = corners[p.id] ?? 'tl';
        return (
          <button
            key={p.id}
            id={`chip-${p.id}`}
            className={[
              'panel',
              `panel-${corner}`,
              turn ? 'is-turn' : '',
              isMe ? 'is-me' : '',
              p.eliminated ? 'is-out' : '',
              seat && !seat.connected && !seat.bot ? 'is-away' : '',
              party.has(p.id) ? 'is-celebrating' : '',
            ].join(' ')}
            style={{ '--seat': SEAT_COLORS[idx] } as React.CSSProperties}
            onClick={() => (isMe ? onPhrases() : !p.eliminated && setTarget(p.id))}
            aria-label={`${seat?.name}: ${money(p.cash)}${p.eliminated ? ', faliu' : ''}${isMe ? ' (você, toque para mandar uma frase)' : ', toque para provocar'}`}
          >
            <span className="panel-avatar" aria-hidden="true">
              <PawnFace pawn={seat?.pawn ?? 0} mood={p.eliminated ? 'sad' : party.has(p.id) ? 'happy' : 'idle'} />
              {seat?.bot && <i className="stamp stamp-bot">BOT</i>}
              {p.eliminated && <i className="stamp stamp-out">FALIU</i>}
            </span>
            <span className="panel-name">{isMe ? 'Você' : seat?.name}</span>
            <span className="panel-cash">
              <Cash value={p.cash} />
              <Coin />
            </span>
            <span className="panel-badges" aria-hidden="true">
              {luckyClover(g, p.id) && (
                <i className="badge-clover" title="Sorte de Malandro: mais chance de carta boa">
                  🍀
                </i>
              )}
              {isUnderdog(g, p.id) && (
                <i className="badge-magnet" title="Ímã do Azarão: as cidades dele puxam quem para na casa antes">
                  🧲
                </i>
              )}
              {p.inJail && <i title="Preso na Ilha">🏝️</i>}
              {p.worldTour && <i title="Vai voar">🛸</i>}
              {[...p.held, ...(p.shop ?? [])].map((h, k) => (
                <i key={k} title={HELD_TEXT[h]}>
                  {HELD_EMOJI[h]}
                </i>
              ))}
              {fx.some((e) => e.kind === 'viralizou') && <i title="Viralizou">📈</i>}
              {seat?.botControlled && !seat.bot && <i title="Jogando no automático">🤖</i>}
              {seat && !seat.connected && !seat.botControlled && !seat.bot && <i title="Desconectado">📵</i>}
            </span>
            {bubbles[p.id] && <span className="bubble">{bubbles[p.id]!.text}</span>}
          </button>
        );
      })}
      {target && <TauntTray target={target} onClose={() => setTarget(null)} />}
    </>
  );
}

/** Dinheiro do placar: conta até o valor novo e mostra quanto entrou ou saiu (como no BT). */
function Cash({ value: target }: { value: number }) {
  // Enquanto o peão anda, o placar espera: o dinheiro só muda quando ele chega na casa.
  const held = useHold();
  const frozen = useRef(target);
  if (!held) frozen.current = target;
  const value = frozen.current;
  const [shown, setShown] = useState(value);
  const [delta, setDelta] = useState<{ n: number; id: number } | null>(null);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    if (start === value) return;
    from.current = value;
    setDelta({ n: value - start, id: Date.now() });
    if (prefs.reducedEffects()) {
      setShown(value);
      return;
    }
    const t0 = performance.now();
    const dur = 700;
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / dur);
      const e = 1 - (1 - k) ** 3;
      setShown(Math.round(start + (value - start) * e));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  useEffect(() => {
    if (!delta) return;
    const t = setTimeout(() => setDelta(null), 1800);
    return () => clearTimeout(t);
  }, [delta]);
  return (
    <>
      <b>{moneyNumber(shown)}</b>
      {delta && (
        <i key={delta.id} className={`cash-delta ${delta.n > 0 ? 'is-up' : 'is-down'}`} aria-hidden="true">
          {delta.n > 0 ? '+' : '−'}
          {moneyNumber(Math.abs(delta.n))}
        </i>
      )}
    </>
  );
}

/** Moeda do placar (desenhada, para ficar nítida em qualquer tamanho). */
export function Coin() {
  return (
    <svg className="coin" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill="#f5b800" stroke="#b07f00" strokeWidth="1.5" />
      <circle cx="10" cy="10" r="6" fill="none" stroke="#ffe066" strokeWidth="1.5" />
      <text x="10" y="13.6" textAnchor="middle" fontSize="9" fontWeight="900" fill="#8a6200" fontFamily="Inter Variable, sans-serif">
        $
      </text>
    </svg>
  );
}

function omit<T extends Record<string, unknown>>(o: T, k: string): T {
  const { [k]: _gone, ...rest } = o;
  return rest as T;
}

export function TauntTray({ target, onClose }: { target: string; onClose: () => void }) {
  const { send, name } = useRoomCtx();
  const throwIt = (item: TauntItem) => send({ t: 'taunt', target, item });
  return (
    <div className="tray-backdrop" onClick={onClose}>
      <div className="tray" role="dialog" aria-label={`Provocar ${name(target)}`} onClick={(e) => e.stopPropagation()}>
        <div className="tray-tabs">
          <span className="is-on">Jogar em {name(target)}</span>
        </div>
        <div className="tray-grid">
          {TAUNTS.map((t) => (
            <button key={t.id} className="tray-item" onClick={() => throwIt(t.id)} aria-label={t.name}>
              {ITEM_ART[t.id] ? <img className="tray-img" src={ITEM_ART[t.id]!.item} alt="" draggable={false} /> : <span aria-hidden="true">{t.emoji}</span>}
            </button>
          ))}
        </div>
        <button className="pill pill-gray pill-block" onClick={onClose}>
          Pronto
        </button>
      </div>
    </div>
  );
}

export function PhraseTray({ onClose }: { onClose: () => void }) {
  const { send } = useRoomCtx();
  return (
    <div className="tray-backdrop" onClick={onClose}>
      <div className="tray" role="dialog" aria-label="Mandar uma frase" onClick={(e) => e.stopPropagation()}>
        <div className="tray-tabs">
          <span className="is-on">Frases</span>
        </div>
        <div className="phrase-grid">
          {(Object.keys(PHRASE_TEXT) as Phrase[]).map((ph) => (
            <button
              key={ph}
              className="phrase-btn"
              onClick={() => {
                send({ t: 'phrase', phrase: ph });
                onClose();
              }}
            >
              {PHRASE_TEXT[ph]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
